import React, { useMemo } from 'react';
import {
	DATE_AND_TIME,
	PortActionTypes,
} from '@shared/utils/constants';
import { isLoadPort } from '@shared/utils/cargoPortHelpers';
import type { CargoPortWithTurnDays } from '@api/features/laytime/getCargosAndLaytimes';
import Table from '@client/components/Table/Table';
import { useAuth } from '@client/lib/auth';
import Card from '@client/components/Card/Card';
import { formatDate } from '@client/utils/formatDate';

const FILTER_LIST = [
	PortActionTypes.LOADING,
	PortActionTypes.DISCHARGING,
	PortActionTypes.BUNKER_STEM,
	PortActionTypes.ARRIVAL,
	PortActionTypes.DEPARTURE,
] as string[];

type Props = {
	selectedCargoPort: CargoPortWithTurnDays;
};

const LaytimeActivities = ({
	selectedCargoPort,
}: Props) => {
	const auth = useAuth();

	const activePortActivities = useMemo(() => {
		if (!auth.userInfo) {
			return [];
		}

		const list = auth.userInfo.portActivities.filter((a) => a.laytime);

		return list.map((a) => a.name.toLowerCase());
	}, [auth]);

	const isLoading = isLoadPort(selectedCargoPort);

	const activities = useMemo(() => {
		if (!selectedCargoPort.activities) {
			return [];
		}

		const actions = selectedCargoPort.activities.map((pca) => {
			return ({
				date: pca.date,
				name: pca.event,
			});
		});

		const extraFields = isLoading ? [
			'Commenced loading',
			'Completed loading',
		] : [
			'Commenced discharging',
			'Completed discharging',
		];

		const filterActions = [
			...FILTER_LIST,
			...extraFields,
		].map((a) => a.toLowerCase());

		const relevantActions = actions.filter((a) => !filterActions.includes(a.name.toLowerCase()));

		return relevantActions
			.filter((a) => activePortActivities.includes(a.name.toLowerCase()));
	}, [activePortActivities, isLoading, selectedCargoPort]);

	return (
		<Card
			title="Port activities"
			padding={0}
			size="small"
		>
			<Table
				dataSource={activities}
				size="small"
				emptyText="Port activities added to the port call will appear here"
				columns={[
					{
						title: 'Name',
						dataIndex: 'name',
						width: 225,
					},
					{
						title: 'Date',
						dataIndex: 'date',
						render: (v) => (v ? formatDate(v, DATE_AND_TIME) : '-'),
					},
				]}
				pagination={false}
			/>
		</Card>
	);
};

export default LaytimeActivities;
